import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { useOperator } from '../../contexts/OperatorContext';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import formatOrderId from '../../utils/formatOrderId';

const { FiFilter, FiSearch, FiClock, FiCheckCircle, FiFileText, FiUser, FiCalendar, FiAlertCircle, FiEye } = FiIcons;

const AssignedOrders = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { assignedOrders, fetchAssignedOrders, operatorProfile } = useOperator();
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('latest');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showCustomerModal, setShowCustomerModal] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        await fetchAssignedOrders();
      } catch (e) {
        console.error('Error loading assigned orders:', e);
      } finally {
        setLoading(false);
      }
    };
    if (operatorProfile) {
      load();
    } else {
      setLoading(false);
    }
  }, [fetchAssignedOrders, operatorProfile]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 bg-green-100';
      case 'in_progress':
        return 'text-blue-600 bg-blue-100';
      case 'admin_review':
      case 'pdf_submitted':
        return 'text-purple-600 bg-purple-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return FiCheckCircle;
      case 'in_progress':
        return FiClock;
      case 'admin_review':
      case 'pdf_submitted':
        return FiFileText;
      default:
        return FiClock;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  // Filter and sort orders (map Supabase fields)
  const safeOrders = Array.isArray(assignedOrders) ? assignedOrders : [];
  const filteredOrders = safeOrders.filter(order => {
    const oid = (String(order.order_id || order.id || '')).toLowerCase();
    const cname = (order.customer_name || '').toLowerCase();
    const stype = (order.service_type || '').toLowerCase();
    const matchesSearch = oid.includes(searchTerm.toLowerCase()) || cname.includes(searchTerm.toLowerCase()) || stype.includes(searchTerm.toLowerCase());
    const status = order.status || 'assigned';
    const matchesStatus = statusFilter === 'all' || status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const coalesceDate = (d) => (d ? new Date(d) : new Date(0));

  const sortedOrders = [...filteredOrders].sort((a, b) => {
    if (sortBy === 'latest') {
      return coalesceDate(b.assigned_at || b.created_at) - coalesceDate(a.assigned_at || a.created_at);
    } else if (sortBy === 'oldest') {
      return coalesceDate(a.assigned_at || a.created_at) - coalesceDate(b.assigned_at || b.created_at);
    } else if (sortBy === 'deadline') {
      return coalesceDate(a.deadline || a.assigned_at || a.created_at) - coalesceDate(b.deadline || b.assigned_at || b.created_at);
    }
    return 0;
  });

  // Check if an order is urgent (deadline within 3 hours)
  const isUrgent = (order) => {
    if (order.status !== 'in_progress') return false;
    const deadline = new Date(order.deadline || Date.now());
    const now = new Date();
    const diffHours = (deadline - now) / (1000 * 60 * 60);
    return diffHours <= 3;
  };

  const handleViewCustomerDetails = (order) => {
    setSelectedOrder(order);
    setShowCustomerModal(true);
  };

  const handleProcessOrder = (orderId) => {
    navigate(`/operator/order/${orderId}`);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-gray-900">Assigned Applications</h1>
          <p className="text-gray-600 mt-2">
            Manage and track all your assigned application orders
          </p>
        </motion.div>

        <Card className="mb-8">
          <div className="p-6">
            {/* Filters and Search */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <SafeIcon
                    icon={FiSearch}
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  />
                  <input
                    type="text"
                    placeholder="Search orders..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiFilter} className="text-gray-500" />
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="all">All Status</option>
                    <option value="in_progress">In Progress</option>
                    <option value="pdf_submitted">PDF Submitted</option>
                    <option value="completed">Completed</option>
                    <option value="assigned">Assigned</option>
                  </select>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">Sort by:</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="latest">Latest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="deadline">Deadline</option>
                </select>
              </div>
            </div>

            {/* Orders Table */}
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
              </div>
            ) : sortedOrders.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full table-fixed">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-36">
                        Order ID
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-56">
                        Customer
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-56">
                        Service
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">
                        Assigned
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                        Amount
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {sortedOrders.map((order) => (
                      <tr key={order.id} className={isUrgent(order) ? 'bg-yellow-50' : ''}>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 font-mono">
                          {formatOrderId(order.order_id || order.id, { date: order.assigned_at || order.created_at })}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                          <div className="flex items-center">
                            <SafeIcon icon={FiUser} className="w-4 h-4 text-gray-400 mr-1" />
                            <span className="truncate block max-w-[200px]">{order.customer_name || '-'}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                          <span className="truncate block max-w-[220px]">{order.service_type || '-'}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(order.status || 'assigned')}`}>
                            <SafeIcon icon={getStatusIcon(order.status)} className="w-3 h-3 mr-1" />
                            {(order.status || 'assigned').replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                          <div className="flex items-center">
                            <SafeIcon icon={FiCalendar} className="w-4 h-4 text-gray-400 mr-1" />
                            {formatDate(order.assigned_at || order.created_at)}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          ₹{Number(order.application_fee || 0) + Number(order.service_fee || 0)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-3">
                            <button
                              onClick={() => handleViewCustomerDetails(order)}
                              className="text-primary-600 hover:text-primary-900"
                            >
                              <SafeIcon icon={FiEye} className="w-5 h-5" />
                            </button>
                            <Button size="sm" onClick={() => handleProcessOrder(order.order_id)}>
                              Process
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-600">No assigned orders yet.</div>
            )}
          </div>
        </Card>

        {/* Modal omitted for brevity; existing modal can display selectedOrder fields */}
      </div>
    </div>
  );
};

export default AssignedOrders;